import { Modal, Button, Form, InputNumber, Typography } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import Draggable from 'react-draggable';
import { TbX, TbArrowsMove } from 'react-icons/tb';
import { useMainContext } from '../../../hooks/useMainContext';
import {
    DEFAULT_INTERNAL_WALL_LENGTH,
    DEFAULT_INTERNAL_WALL_WIDTH,
    SCALEFACTOR_FOR_CONVERSION,
} from '../../../constant';

export const InternalWallForm = observer(() => {
    const { closetManager } = useMainContext();
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (closetManager.shouldInternalWallFormOpen()) {
            setModalPosition({ x: 0, y: 0 });
        }
    }, [closetManager.shouldInternalWallFormOpen()]);

    const { control, handleSubmit, reset, setValue } = useForm({
        defaultValues: {
            length: DEFAULT_INTERNAL_WALL_LENGTH,
            depth: DEFAULT_INTERNAL_WALL_WIDTH,
            rotation: 0,
        },
    });

    useEffect(() => {
        const wall = closetManager.internalWallData;
        if (wall) {
            const scaleFactor =
                closetManager.getScaleFactorForAnnotationDisplayByClosetId(
                    closetManager.selectedPageNo,
                    wall.closetId,
                    true,
                );
            setValue('rotation', parseFloat(wall.rotation || 0).toFixed(2));
            setValue(
                'length',
                parseFloat(
                    wall.length / (SCALEFACTOR_FOR_CONVERSION * scaleFactor),
                ).toFixed(2),
            );
            setValue(
                'depth',
                parseFloat(
                    wall.depth / (SCALEFACTOR_FOR_CONVERSION * scaleFactor),
                ).toFixed(2),
            );
        } else {
            reset();
        }
    }, [closetManager.internalWallData, reset]);

    const handleInchChange = (field, value) => {
        const parsed = parseFloat(value) || 0;
        setValue(field, parseFloat(parsed.toFixed(2)));
    };

    const onSubmit = (data) => {
        if (data.length <= 0 || data.depth <= 0) return;

        const wallData = {
            length: parseFloat(data.length).toFixed(2),
            depth: parseFloat(data.depth).toFixed(2),
            rotation: parseFloat(data.rotation).toFixed(2),
        };

        if (!closetManager.updateSelectedInternalWall(wallData)) return;
        closetManager.setInternalForm(false);
        closetManager.drawNewClosets();
        reset();
        closetManager.setInternalWallData(null);
    };

    const closeModal = () => {
        closetManager.setInternalForm(false);
        reset();
        closetManager.setInternalWallData(null);
    };

    return (
        <Modal
            className="internal-wall-form-modal"
            width={370}
            centered
            styles={{ content: { padding: '16px', borderRadius: '20px' } }}
            modalRender={(modal) => (
                <Draggable
                    handle=".drag-button"
                    cancel=".ant-input-number, .ant-btn:not(.drag-button)"
                    position={modalPosition}
                    onStop={(_, data) =>
                        setModalPosition({ x: data.x, y: data.y })
                    }>
                    <div>{modal}</div>
                </Draggable>
            )}
            onCancel={closeModal}
            footer={null}
            closable={false}
            open={closetManager.shouldInternalWallFormOpen()}>
            <div className="internal-wall-form-wrapper">
                <div
                    className="border-b-[1px] border-theme-border pb-2"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 10,
                    }}>
                    <Typography.Title level={4} style={{ margin: 0 }}>
                        Internal Wall Form
                    </Typography.Title>
                    <div style={{ display: 'flex' }}>
                        <Button
                            className="drag-button flex items-center justify-center p-0 w-[32px] h-[32px]"
                            style={{ cursor: 'move', border: 'none' }}>
                            <TbArrowsMove size={20} />
                        </Button>
                        <Button
                            type="text"
                            className="flex items-center justify-center p-0 w-[32px] h-[32px]"
                            onClick={closeModal}>
                            <TbX size={20} />
                        </Button>
                    </div>
                </div>

                <Form
                    layout="vertical"
                    className="internal-wall-form"
                    onFinish={handleSubmit(onSubmit)}>
                    <Form.Item label="Length (Width) :">
                        <Controller
                            name="length"
                            control={control}
                            rules={{
                                required: 'Required',
                                validate: (v) =>
                                    v > 0 || 'Length must be greater than zero',
                            }}
                            render={({ field, fieldState }) => (
                                <>
                                    <InputNumber
                                        {...field}
                                        placeholder="inches"
                                        addonAfter={'"'}
                                        className="number-input"
                                        min={0}
                                        step={0.01}
                                        precision={2}
                                        onChange={(val) =>
                                            handleInchChange('length', val)
                                        }
                                        status={
                                            fieldState.invalid ? 'error' : ''
                                        }
                                    />
                                    {fieldState.error && (
                                        <p className="error-message">
                                            {fieldState.error.message}
                                        </p>
                                    )}
                                </>
                            )}
                        />
                    </Form.Item>

                    <Form.Item label="Depth (Height) :">
                        <Controller
                            name="depth"
                            control={control}
                            rules={{
                                required: 'Required',
                                validate: (v) =>
                                    v > 0 || 'Depth must be greater than zero',
                            }}
                            render={({ field, fieldState }) => (
                                <>
                                    <InputNumber
                                        {...field}
                                        placeholder="inches"
                                        addonAfter={'"'}
                                        className="number-input"
                                        min={0}
                                        step={0.01}
                                        precision={2}
                                        onChange={(val) =>
                                            handleInchChange('depth', val)
                                        }
                                        status={
                                            fieldState.invalid ? 'error' : ''
                                        }
                                    />
                                    {fieldState.error && (
                                        <p className="error-message">
                                            {fieldState.error.message}
                                        </p>
                                    )}
                                </>
                            )}
                        />
                    </Form.Item>

                    <Form.Item label="Rotation :">
                        <Controller
                            name="rotation"
                            control={control}
                            rules={{
                                validate: (v) =>
                                    (v >= 0 && v <= 360) ||
                                    'Rotation must be between 0 and 360',
                            }}
                            render={({ field, fieldState }) => (
                                <InputNumber
                                    {...field}
                                    placeholder="degrees"
                                    addonAfter="°"
                                    className="number-input"
                                    onChange={(val) =>
                                        handleInchChange('rotation', val)
                                    }
                                    step={1}
                                    precision={0}
                                    status={fieldState.invalid ? 'error' : ''}
                                />
                            )}
                        />
                    </Form.Item>

                    <div className="form-actions">
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="bg-theme-orange text-theme-white !!hover:bg-theme-orange-hover !shadow-none save-changes-button">
                            OK
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
});
